import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import NoSSR from 'react-no-ssr';
import { ATTRIBUTION_MODE_CHERRYPICK } from '../../../contexts/AbstractContext';
import { JobContextProvider } from '../../../contexts/JobContext';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import { CONVERT_CONTENT } from '../../../pages/CompanyPages/CompanyOverview/CompanyOverview';
import { getCloudinaryImagePathById } from '../../../utilities/images';
import Pixel, {
    EmitMetric,
} from '../../Analytics/VisibilityPixel/VisibilityPixel';
import PieChartPageSection from '../../Charts/PieChartPageSection/PieChartPageSection';
import SingleReviewComponent, {
    SINGLE_REVIEW,
} from '../../Community/SingleReviewComponent/SingleReviewComponent';
import LoadingShineComponent from '../../Content/Feed/LoadingShineComponent/LoadingShineComponent';
import Badge from '../../Employer/Badge/Badge';
import Button from '../../Input/Button/Button';
import Slider from '../../JobComponents/Silder/Slider';
import Rating from '../../JobV3/Elements/Rating/Rating';
import CommonCardShell from '../../Layout/CommonCardShell/CommonCardShell';
import CompanyIcon from '../../Layout/CompanyHeaderV2/CompanyIcon/CompanyIcon';
import ListSection from '../../Layout/ListSection/ListSection';
import JobListing from '../../JobV3/Elements/JobListing/JobListing';

import './SponsoredCompanyBox.scss';

const API_SPONSORED_COMPANY = '/api/company/sponsored-company-overview';

const getParamValue = (key) => {
    const searchParams = new URL(window.location.href).searchParams;
    return searchParams.get(key);
};

export class SponsoredCompanyBox extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            companyPixels: null,
            company: null,
            companyReviews: [],
            companyBadges: [],
            sponsoredCompanyDescription: null,
            companyCulture: null,
            womenJobReviews: [],
            socialMediaAccounts: null,
            genderDiversity: null,
            companyVideo: null,
            crowdSourcedBenefitData: [],
            employees: [],
            companyArticles: [],
            companySlides: [],
            jobs: [],
            topics: [],
        };
    }

    static propTypes = {
        session: PropTypes.object,
        sponsoredCompany: PropTypes.object,
    };

    logo = () => {
        const { company } = this.state;
        company.logo.src =
            company.logo && company.logo.src
                ? getCloudinaryImagePathById(company.logo.src, 'large')
                : '';
        return (
            <div className="logo-section">
                <a href={`/company-overview/${company.companyUrl}`}>
                    <CompanyIcon logo={company.logo} classes={'logo-size-80'} />
                </a>
                {company.rating ? (
                    <div className="rating">
                        <Rating
                            totalWidth={60}
                            rating={Number(company.rating)}
                        />
                    </div>
                ) : null}
            </div>
        );
    };

    badges = () => {
        const dots =
            this.state.companyBadges && this.state.companyBadges.length <= 8;
        const sliderSettings = {
            dots,
            infinite: true,
            arrows: false,
            autoplay: true,
            autoplaySpeed: 5000,
            speed: 1000,
            slidesToShow: 1,
            slidesToScroll: 1,
            centerMode: false,
            adaptiveHeight: false,
            swipe: false,
        };

        return this.state.companyBadges &&
            this.state.companyBadges.length > 0 ? (
            <div className="badge-section">
                <a href={`/company-overview/${this.state.company.companyUrl}`}>
                    <div className={'oswald-size28'}>
                        {this.state.company.companyName}
                    </div>
                </a>
                <div className="silver-divider" />
                <NoSSR>
                    <Slider
                        settings={sliderSettings}
                        className={'custom-slide'}
                    >
                        {this.state.companyBadges.map((badge, idx) => (
                            <div className="badge-div" key={idx}>
                                <a href={badge.url}>
                                    <Badge type={badge.type} />
                                </a>
                            </div>
                        ))}
                    </Slider>
                </NoSSR>
            </div>
        ) : null;
    };

    handleCompanyClickMetric = () =>
        EmitMetric({
            misc_event_count: 1,
            misc_event_type: 'right-rail-sponsored-company-click',
        });

    renderJobs = () => {
        const { jobs } = this.state;
        const { session } = this.props;

        return (
            <JobContextProvider
                content={jobs}
                session={session}
                attributionMode={ATTRIBUTION_MODE_CHERRYPICK}
            >
                {jobs.map((job) => (
                    <JobListing
                        key={job.hash}
                        {...job}
                        onClick={this.handleCompanyClickMetric}
                    />
                ))}
            </JobContextProvider>
        );
    };

    jobs = () => {
        const { jobs, company } = this.state;
        const allJobsUrl = jobs && jobs.length > 0 ? jobs[0].company.url : '';
        const metric = {
            misc_event_count: 1,
            misc_event_type: 'right-rail-see-all-jobs-click',
        };

        return jobs && jobs.length > 0 ? (
            <div className="jobs-section">
                <div className={'oswald-size28'}>
                    Jobs at&nbsp;
                    <a href={`/company/jobs/${company.companyUrl}`}>
                        {company.companyName}
                    </a>
                </div>

                {this.renderJobs()}

                <Button
                    type="purple-medium-white"
                    className="btn-wrapper"
                    inputStyle="btn-style"
                    onClick={() => {
                        if (metric) EmitMetric(metric);
                        window.location.href = allJobsUrl;
                    }}
                    value={'See all jobs >'}
                />
            </div>
        ) : null;
    };

    womensJobReviewsChartSection = () => {
        const { womenJobReviews } = this.state;
        return womenJobReviews && womenJobReviews.length > 0 ? (
            <div className={'piechart-section'}>
                <div className={'oswald-size28'}>
                    Women's reviews of {this.state.company.companyName}
                </div>
                <div className="silver-divider" />
                <PieChartPageSection chartProps={this.state.womenJobReviews} />
            </div>
        ) : null;
    };

    highlightedReviews = () => {
        const { companyReviews, company } = this.state;

        return companyReviews && companyReviews.length > 0 ? (
            <div className={'review-section'}>
                <div className={'oswald-size28'}>Highlighted reviews</div>
                <div className="silver-divider" />
                <SingleReviewComponent
                    {...companyReviews[0]}
                    company={{
                        ...company,
                        companyEndpoint: company.companyUrl,
                    }}
                    location={SINGLE_REVIEW}
                    expanded={true}
                    googleSeo={null}
                    metric={{
                        misc_event_count: 1,
                        misc_event_type: 'right-rail-full-review-click',
                    }}
                />
            </div>
        ) : null;
    };

    fetchSponsoredCompanyOverview = async () => {
        const isJobSearch = window.location.pathname.includes('/jobs/find');
        const keywords = isJobSearch ? getParamValue('keywords') : '';
        const location = isJobSearch ? getParamValue('location') : '';
        const { sponsoredCompany } = this.props;

        const companyId =
            sponsoredCompany && sponsoredCompany.company_id
                ? `&companyId=${sponsoredCompany.company_id}`
                : '';
        const url = `${API_SPONSORED_COMPANY}?keywords=${keywords}&location=${location}${companyId}`;

        try {
            const res = await axios.get(url);

            if (res.data.data) {
                this.setState({
                    ...res.data.data,
                    companyPixels: JSON.parse(
                        res.data.data.company.trackingPixels
                    ),
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            this.setState({ loading: false });
        }
    };

    mainContent = () => {
        return this.state.company ? (
            <CommonCardShell>
                <div className="component-RightRail-SponsoredCompanyBox">
                    <Pixel
                        metrics={{
                            company_id: [this.state.company.companyId],
                            misc_event_type:
                                'right-rail-sponsored-company-impression',
                            impression: 1,
                        }}
                    />
                    <div
                        className="companyPixels"
                        dangerouslySetInnerHTML={CONVERT_CONTENT(
                            this.state.companyPixels
                                ? this.state.companyPixels.overview
                                : null
                        )}
                    />
                    <ListSection title={'FEATURED COMPANY'}>
                        {this.logo()}
                        {this.badges()}
                        {this.jobs()}
                        {this.womensJobReviewsChartSection()}
                        {this.highlightedReviews()}
                    </ListSection>
                </div>
            </CommonCardShell>
        ) : null;
    };

    componentDidMount() {
        this.fetchSponsoredCompanyOverview();
    }

    render() {
        return this.state.loading ? (
            <LoadingShineComponent />
        ) : (
            this.mainContent()
        );
    }
}

export default (props) => (
    <PageConsumer>
        {({ pageProps }) => (
            <SponsoredCompanyBox {...props} session={pageProps.session} />
        )}
    </PageConsumer>
);
